import { useState } from 'react';

interface Option {
  id: string;
  label: string;
}

/**
 * A select of existing entities with a "＋ New…" escape hatch. When "new" is
 * chosen a text input appears and `onNewName` carries the typed value while
 * `value` is set to the sentinel `NEW`.
 */
export const NEW = '__new__';

export function EntityPicker(props: {
  label: string;
  options: Option[];
  value: string;
  onChange: (id: string) => void;
  newName: string;
  onNewName: (name: string) => void;
  newPlaceholder?: string;
}): React.ReactElement {
  return (
    <div className="field">
      <label>{props.label}</label>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)}>
        <option value="">— select —</option>
        {props.options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        <option value={NEW}>＋ New…</option>
      </select>
      {props.value === NEW && (
        <input
          autoFocus
          placeholder={props.newPlaceholder ?? 'Name'}
          value={props.newName}
          onChange={(e) => props.onNewName(e.target.value)}
        />
      )}
    </div>
  );
}

/** Small hook bundling the id + new-name state a picker needs. */
export function usePickerState(initial = ''): {
  id: string;
  setId: (v: string) => void;
  newName: string;
  setNewName: (v: string) => void;
} {
  const [id, setId] = useState(initial);
  const [newName, setNewName] = useState('');
  return { id, setId, newName, setNewName };
}
